import {Button, IconButton} from '@dropbox/dig-components/buttons';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {atoms, Box, Split} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ArrowUpLine} from '@dropbox/dig-icons/assets';
import {EditorTextSize} from 'components/DSYS/editor/themes/EditorTheme';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {StopSquare} from 'components/DSYS/StopSquare';
import {$getRoot} from 'lexical';
import {
  ClipboardEvent,
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import {$getChatComposerText, $getChatMentions, type ChatMention} from './chatMentions';

export type ChatComposerProps = {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onStop?: () => void;
  isStreaming: boolean;
  placeholder: ReactNode;
  hint?: ReactNode;
  footer?: ReactNode;
  submitLabel?: string;
  disabled?: boolean;
  /** Opt out when the caller clears only after an accepted async submission. */
  clearOnSubmit?: boolean;
  /** Min height of the input area in px. Defaults to a single-line composer. */
  minHeight?: number;
  /** Editor text scale. Defaults to `small`. */
  textSize?: EditorTextSize;
  /** Allow submit while streaming (e.g. to queue a follow-up message). */
  canSubmitWhileStreaming?: boolean;
  /** People-only @mention typeahead. Mentions read as `@Name` in `value`. */
  mentions?: boolean;
  /** The people mentioned in the editor, on every change (with `mentions`). */
  onMentionsChange?: (mentions: ChatMention[]) => void;
  /** Extra buttons before stop/send, e.g. an attach button. */
  actions?: ReactNode;
  /** Called instead of pasting when the clipboard holds only files (e.g. a screenshot). */
  onPasteFiles?: (files: File[]) => void;
};

export type ChatComposerHandle = {
  focus: () => void;
  clear: () => void;
};

export const ChatComposer = forwardRef<ChatComposerHandle, ChatComposerProps>(
  (
    {
      value,
      onChange,
      onSubmit,
      onStop,
      isStreaming,
      placeholder,
      hint,
      footer,
      submitLabel = 'Send',
      disabled = false,
      clearOnSubmit = true,
      minHeight = 40,
      textSize = 'small',
      canSubmitWhileStreaming = false,
      mentions = false,
      onMentionsChange,
      actions,
      onPasteFiles,
    },
    ref
  ) => {
    const inputRef = useRef<{
      focusEditor?: () => void;
      getEditorState?: () => unknown;
      clearEditor?: (resetValue?: string) => void;
    }>(null);
    const [hasInput, setHasInput] = useState(value.trim().length > 0);
    // With mentions on, the editor keeps its own state and only takes `value`
    // when the caller changes it: reloading our own plain-text echo (which the
    // editor does on blur) would turn mention nodes back into text.
    const [initialValue] = useState(value);
    const emittedValueRef = useRef(value);

    useEffect(() => {
      setHasInput(value.trim().length > 0);
    }, [value]);

    useEffect(() => {
      if (!mentions || value === emittedValueRef.current) {
        return;
      }
      emittedValueRef.current = value;
      inputRef.current?.clearEditor?.(value || undefined);
    }, [mentions, value]);

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focusEditor?.(),
      clear: () => {
        inputRef.current?.clearEditor?.();
        setHasInput(false);
      },
    }));

    const handleSubmit = useCallback(() => {
      if (disabled || (isStreaming && !canSubmitWhileStreaming)) {
        return;
      }
      onSubmit();
      if (clearOnSubmit) {
        inputRef.current?.clearEditor?.();
        setHasInput(false);
      }
    }, [canSubmitWhileStreaming, clearOnSubmit, disabled, isStreaming, onSubmit]);

    return (
      <Box
        backgroundColor="Background Base"
        borderStyle="Solid"
        borderColor="Border Subtle"
        borderWidth="1"
        borderRadius="XLarge"
        boxShadow="Floating"
        style={{flexShrink: 0, overflow: 'hidden'}}
        // Capture runs before the editor sees the paste. Text wins when both are
        // present (spreadsheet copies carry an image too).
        onPasteCapture={(event: ClipboardEvent) => {
          const files = Array.from(event.clipboardData.files);
          if (!onPasteFiles || !files.length || event.clipboardData.getData('text/plain')) {
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          onPasteFiles(files);
        }}
      >
        <Box
          display="flex"
          alignItems="flex-start"
          paddingX="12"
          paddingTop="12"
          paddingBottom="4"
          style={{gap: 12}}
        >
          <Box style={{flex: 1, minWidth: 0}}>
            <RichTextArea
              disableBorder
              ref={inputRef}
              value={mentions ? initialValue : value}
              editable={!disabled}
              paddingX="0"
              textColor="inherit"
              textSize={textSize}
              disableTables
              plainText
              hideToolbar
              not100
              minHeight={minHeight}
              maxHeight="min(30vh, 420px)"
              focusNextOnTabAtBlockEnd
              onEnterSubmit={() => {
                handleSubmit();
              }}
              placeholder={placeholder}
              peopleMentions={mentions}
              onChange={(state) => {
                let text = '';
                let picked: ChatMention[] = [];
                try {
                  state.read(() => {
                    text = $getChatComposerText();
                    picked = mentions ? $getChatMentions() : [];
                  });
                } catch {
                  // ignore
                }
                setHasInput(text.trim().length > 0);
                emittedValueRef.current = text;
                onChange(text);
                if (mentions) {
                  onMentionsChange?.(picked);
                }
              }}
            />
          </Box>
          <Box flexShrink={0} display="flex" alignItems="center" style={{gap: 4}}>
            {actions}
            {isStreaming && onStop && (
              <Tooltip placement="top" title="Stop">
                <IconButton
                  variant="borderless"
                  size="small"
                  onClick={onStop}
                  aria-label="Stop"
                  className={atoms({backgroundColor: 'Opacity Surface'})}
                >
                  <StopSquare className={atoms({color: 'Text Base'})} />
                </IconButton>
              </Tooltip>
            )}
            {(!isStreaming || canSubmitWhileStreaming) && (
              <IconButton
                variant={!hasInput || disabled ? 'opacity' : 'primary'}
                size="small"
                onClick={handleSubmit}
                disabled={!hasInput || disabled}
                aria-label={submitLabel}
              >
                <UIIcon
                  src={ArrowUpLine}
                  className={atoms({
                    color: !hasInput || disabled ? 'Border Base' : 'Background Base',
                  })}
                />
              </IconButton>
            )}
          </Box>
        </Box>
        {(hint || footer) && (
          <Box
            display="flex"
            paddingX="12"
            paddingTop="4"
            paddingBottom="12"
            justifyContent="space-between"
            alignItems="center"
            style={{gap: 8}}
          >
            <Box display="flex" justifyContent="flex-end" flexGrow={1}>
              {hint}
            </Box>
            {footer}
          </Box>
        )}
      </Box>
    );
  }
);
ChatComposer.displayName = 'ChatComposer';

/** Variant with a primary action button row (e.g. data agent "Ask" button). */
export type ChatComposerWithActionProps = Omit<ChatComposerProps, 'onSubmit'> & {
  onSubmit: () => void;
  actionLabel: string;
  actionIcon?: ReactNode;
  onStop?: () => void;
};

export const ChatComposerWithAction = ({
  value,
  onChange,
  onSubmit,
  onStop,
  isStreaming,
  placeholder,
  hint,
  actionLabel,
  actionIcon,
  disabled = false,
}: ChatComposerWithActionProps) => {
  const inputRef = useRef<{clearEditor?: (resetValue?: string) => void}>(null);
  const [hasInput, setHasInput] = useState(value.trim().length > 0);

  const handleSubmit = useCallback(() => {
    if (isStreaming || disabled || !value.trim()) {
      return;
    }
    onSubmit();
    inputRef.current?.clearEditor?.();
    setHasInput(false);
  }, [disabled, isStreaming, onSubmit, value]);

  return (
    <Box
      backgroundColor="Background Base"
      borderStyle="Solid"
      borderColor="Border Subtle"
      borderWidth="1"
      borderRadius="XLarge"
      boxShadow="Floating"
      style={{flexShrink: 0, overflow: 'hidden'}}
    >
      <Box padding="8">
        <Box paddingX="4">
          <RichTextArea
            disableBorder
            ref={inputRef}
            value={value}
            editable={!isStreaming && !disabled}
            paddingX="0"
            textColor="inherit"
            textSize="small"
            disableTables
            plainText
            hideToolbar
            not100
            minHeight={60}
            maxHeight="min(30vh, 420px)"
            focusNextOnTabAtBlockEnd
            onEnterSubmit={() => {
              handleSubmit();
            }}
            placeholder={placeholder}
            onChange={(state) => {
              let text = '';
              try {
                state.read(() => {
                  text = $getRoot().getTextContent();
                });
              } catch {
                // ignore
              }
              setHasInput(text.trim().length > 0);
              onChange(text);
            }}
          />
        </Box>
        <Split alignY="center" paddingTop="8" paddingX="4">
          <Split.Item width="fill">{hint}</Split.Item>
          <Split.Item>
            <Box display="flex" alignItems="center" style={{gap: 8}}>
              {isStreaming && onStop ? (
                <Button variant="opacity" onClick={onStop}>
                  Stop
                </Button>
              ) : null}
              <Button
                variant="primary"
                onClick={handleSubmit}
                isLoading={isStreaming}
                disabled={isStreaming || disabled || !hasInput}
                withIconStart={actionIcon}
              >
                {actionLabel}
              </Button>
            </Box>
          </Split.Item>
        </Split>
      </Box>
    </Box>
  );
};
